import axios from 'axios';
import { message } from 'antd';
import config from '../config';

const api = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
});

// 请求拦截器：自动附加 Authorization（优先管理员令牌）
api.interceptors.request.use(
  (cfg) => {
    const adminToken = localStorage.getItem('admin_token');
    const userToken = localStorage.getItem('token');
    const token = adminToken || userToken;
    if (token) {
      cfg.headers = cfg.headers || {};
      cfg.headers['Authorization'] = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error)
);

// 响应拦截器
api.interceptors.response.use(
  (response) => {
    // 对于文件下载，保留完整响应以读取 headers 和 data
    if (response.config && response.config.responseType === 'blob') {
      // 确保返回完整的response对象，包含data和headers
      return response;
    }
    return response.data;
  },
  (error) => {
    if (error.response) {
      const { status, config } = error.response;
      const url = config.url;

      // 对于获取AI提供商列表时的404错误，不弹出提示
      if (status === 404 && url.includes('/ai-providers')) {
        console.warn('AI提供商列表资源不存在，可能列表为空。');
        return Promise.resolve({ data: [] }); // 返回空数据，让页面正常渲染空列表
      }
      
      // 对于EAN配置相关的错误，提供更详细的信息
      if (url.includes('/ean-config')) {
        console.error('EAN配置API错误:', {
          status,
          url,
          response: error.response.data
        });
        
        // 对于删除操作的404错误，可能是因为记录已经被删除
        if (status === 404 && url.includes('DELETE')) {
          console.warn('删除的配置可能已经不存在，但操作可能已成功');
          return Promise.resolve({ message: '删除成功' });
        }
      }

      switch (status) {
        case 403:
          message.error('没有权限访问该资源');
          break;
        case 404:
          message.error('请求的资源不存在');
          break;
        case 500:
          message.error('服务器错误');
          break;
        default:
          message.error(error.response.data.message || '请求失败');
      }
    } else {
      // 检查是否是EAN配置删除操作的网络错误
      if (error.config && error.config.url && error.config.url.includes('/ean-config') && error.config.method === 'delete') {
        console.warn('EAN配置删除操作遇到网络错误，但操作可能已成功');
        // 不显示错误消息，让组件自行处理
        return Promise.reject(new Error('DELETE_NETWORK_ERROR'));
      }
      message.error('网络错误，请检查您的网络连接');
    }
    return Promise.reject(error);
  }
);

// 产品相关API
export const productApi = {
  // 获取产品列表
  getProducts: (params) => api.get('/products/', { params }),
  
  // 获取单个产品
  getProduct: (id) => api.get(`/products/${id}`),
  
  // 创建产品
  createProduct: (data) => api.post('/products', data),
  
  // 更新产品
  updateProduct: (id, data) => api.put(`/products/${id}`, data),
  
  // 删除产品
  deleteProduct: (id) => api.delete(`/products/${id}`),
};

// 流量池相关API
export const trafficPoolApi = {
  // 获取流量池列表
  getPools: (params) => api.get('/traffic-pools/', { params }),
  
  // 获取单个流量池
  getPool: (id) => api.get(`/traffic-pools/${id}`),
  
  // 创建流量池
  createPool: (data) => api.post('/traffic-pools/', data),

  // 批量创建（每行一个关键词）
  createPoolsBatch: (data) => api.post('/traffic-pools/batch', data),
  
  // 更新流量池
  updatePool: (id, data) => api.put(`/traffic-pools/${id}`, data),
  
  // 删除流量池
  deletePool: (id) => api.delete(`/traffic-pools/${id}`),
  
  // 删除所有流量池
  deleteAllPools: () => api.delete('/traffic-pools/delete-all'),
  
  // 导入流量池Excel文件
  importPools: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/traffic-pools/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
};

// 模板相关API
export const templateApi = {
  // 获取模板列表
  getTemplates: (params) => api.get('/templates/', { params }),
  
  // 获取单个模板
  getTemplate: (id) => api.get(`/templates/${id}`),
  
  // 创建模板
  createTemplate: (data) => api.post('/templates', data),
  
  // 更新模板
  updateTemplate: (id, data) => api.put(`/templates/${id}`, data),
  
  // 删除模板
  deleteTemplate: (id) => api.delete(`/templates/${id}`),
  
  // 应用模板
  applyTemplate: (id, data) => api.post(`/templates/${id}/apply`, data),
  
  // 复制模板
  copyTemplate: (id) => api.post(`/templates/${id}/copy`),
};

// 产品类型相关API
export const productTypeApi = {
  // 获取产品类型列表
  getProductTypes: (params) => api.get('/product-types/', { params }),
  
  // 获取单个产品类型
  getProductType: (id) => api.get(`/product-types/${id}`),
  
  // 创建产品类型
  createProductType: (data) => api.post('/product-types', data),
  
  // 更新产品类型
  updateProductType: (id, data) => api.put(`/product-types/${id}`, data),
  
  // 删除产品类型
  deleteProductType: (id) => api.delete(`/product-types/${id}`),
};

// 文件相关API
export const fileApi = {
  // 上传文件
  uploadFile: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // 下载文件
  downloadFile: (id) => api.get(`/files/${id}/download`, { responseType: 'blob' }),
  
  // 删除文件
  deleteFile: (id) => api.delete(`/files/${id}`),
};

// AI提供商管理
export const aiProviderApi = {
  // 获取AI提供商列表
  getProviders: () => api.get('/ai-providers'),
  
  // 获取单个AI提供商
  getProvider: (id) => api.get(`/ai-providers/${id}`),
  
  // 创建AI提供商（勿使用尾部斜杠：POST /ai-providers/ 会触发 307 重定向，易导致请求失败）
  createProvider: (data) => api.post('/ai-providers', data),
  
  // 更新AI提供商
  updateProvider: (id, data) => api.put(`/ai-providers/${id}`, data),
  
  // 删除AI提供商
  deleteProvider: (id) => api.delete(`/ai-providers/${id}`),
  
  // 测试AI提供商
  testProvider: (id) => api.post(`/ai-providers/${id}/test`),
};

// EAN配置管理
export const eanConfigApi = {
  // 获取EAN配置列表
  getConfigs: (params) => api.get('/ean-config/', { params }),
  
  // 获取单个EAN配置
  getConfig: (id) => api.get(`/ean-config/${id}/`),
  
  // 获取激活的EAN配置
  getActiveConfig: () => api.get('/ean-config/active/'),
  
  // 创建EAN配置
  createConfig: (data) => api.post('/ean-config/', data, {
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  
  // 更新EAN配置
  updateConfig: (id, data) => api.put(`/ean-config/${id}`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  
  // 删除EAN配置
  deleteConfig: (id) => api.delete(`/ean-config/${id}/`),
  
  // 激活EAN配置
  activateConfig: (id) => api.post(`/ean-config/${id}/activate/`),
};

// SKU配置管理
export const skuConfigApi = {
  // 获取SKU配置列表
  getConfigs: (params) => api.get('/sku-config/', { params }),
  
  // 获取单个SKU配置
  getConfig: (id) => api.get(`/sku-config/${id}`),
  
  // 获取激活的SKU配置
  getActiveConfig: () => api.get('/sku-config/active'),
  
  // 创建SKU配置
  createConfig: (data) => api.post('/sku-config/', data, {
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  
  // 更新SKU配置
  updateConfig: (id, data) => api.put(`/sku-config/${id}`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  
  // 删除SKU配置
  deleteConfig: (id) => api.delete(`/sku-config/${id}`),
  
  // 激活SKU配置
  activateConfig: (id) => api.post(`/sku-config/${id}/activate`),
};

// 库存金额处理API
export const inventoryAmountApi = {
  // 配置管理
  // 获取配置列表
  getConfigs: (params) => api.get('/inventory-amount/config', { params }),
  
  // 获取单个配置
  getConfig: (id) => api.get(`/inventory-amount/config/${id}`),
  
  // 获取激活的配置
  getActiveConfig: (region) => api.get('/inventory-amount/config/active', { params: { region } }),
  
  // 创建配置
  createConfig: (data) => api.post('/inventory-amount/config', data, {
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  
  // 更新配置
  updateConfig: (id, data) => api.put(`/inventory-amount/config/${id}`, data, {
    headers: {
      'Content-Type': 'application/json',
    },
  }),
  
  // 删除配置
  deleteConfig: (id) => api.delete(`/inventory-amount/config/${id}`),
  
  // 激活配置
  activateConfig: (id) => api.post(`/inventory-amount/config/${id}/activate`),
  
  // 文件处理
  // 处理库存金额数据
  processFiles: (formData) => api.post('/inventory-amount/process', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60 * 60 * 1000, // 60分钟超时
  }),
  
  // 下载处理结果
  downloadResult: (sessionId, fileType = 'txt') => api.get(`/inventory-amount/result/${sessionId}/download`, {
    params: { file_type: fileType },
    responseType: 'blob',
    timeout: 60 * 60 * 1000
  }),
  
  // 获取处理历史
  getHistory: (params) => api.get('/inventory-amount/history', { params }),
};

// 库存金额处理（旧）：两文件 + 内置匹配模板；配置复用 inventoryAmountApi
export const inventoryAmountLegacyApi = {
  processFiles: (formData) => api.post('/inventory-amount-legacy/process', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    timeout: 60 * 60 * 1000,
  }),
  downloadResult: (sessionId, fileType = 'txt') => api.get(`/inventory-amount-legacy/result/${sessionId}/download`, {
    params: { file_type: fileType },
    responseType: 'blob',
    timeout: 60 * 60 * 1000,
  }),
  getHistory: (params) => api.get('/inventory-amount-legacy/history', { params }),
};

// 设置管理API
api.settings = {
  // 获取所有设置
  getAll: () => api.get('/settings/'),
  
  // 更新通用设置
  update: (data) => api.put('/settings', data),
  
  // 更新邮件设置
  updateEmail: (data) => api.put('/settings/email', data),
  
  // 更新AI设置
  updateAI: (data) => api.put('/settings/ai', data),
  
  // 更新F-SKU设置
  updateSKU: (data) => api.put('/settings/sku', data),
  
  // 更新亚马逊设置
  updateAmazon: (data) => api.put('/settings/amazon', data),
  
  // 测试邮件
  testEmail: () => api.post('/settings/test-email'),
  
  // 测试亚马逊API
  testAmazonAPI: () => api.post('/settings/test-amazon'),
};

// 仪表盘相关API
export const dashboardApi = {
  // 获取仪表盘统计数据
  getStats: () => api.get('/dashboard/stats'),
  
  // 获取最近活动
  getRecentActivities: (limit = 10) => api.get('/dashboard/recent-activities', { params: { limit } }),
  
  // 获取数据处理历史
  getProcessingHistory: (limit = 5) => api.get('/dashboard/processing-history', { params: { limit } }),
  
  // 获取系统健康状态
  getSystemHealth: () => api.get('/dashboard/system-health'),
};

// 数据处理相关API
export const dataProcessingApi = {
  // 获取处理状态
  getStatus: () => api.get('/data-processing/status'),
  
  // 导入Excel文件
  importExcel: (file, language = 'english') => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('language', language);
    return api.post('/data-processing/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // 导入处理可能持续较久，这里将超时提升到60分钟
      timeout: 60 * 60 * 1000,
    });
  },
  
  // 导入Excel文件用于模板匹配（不进行数据处理）
  importExcelForTemplateMatching: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/data-processing/import-for-template-matching', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      timeout: 5 * 60 * 1000, // 5分钟超时，因为不进行数据处理
    });
  },
  
  // 停止处理
  cancelProcessing: () => api.post('/data-processing/cancel-processing'),

  // 删除暂停/僵尸中的处理任务
  deleteProcessingTask: () => api.post('/data-processing/delete-processing-task'),

  // 同步导入Excel文件
  importExcelSync: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/data-processing/import-sync', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      // 同步导入同样提升超时时间到60分钟
      timeout: 60 * 60 * 1000,
    });
  },
  
  // 导出处理后的数据
  exportExcel: () => api.get('/data-processing/export', { responseType: 'blob', timeout: 60 * 60 * 1000 }),
  
  // 获取数据预览
  getPreview: () => api.get('/data-processing/preview'),
  
  // 获取当前模板
  getCurrentTemplate: () => api.get('/data-processing/current-template'),
  
  // 上传模板
  uploadTemplate: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/data-processing/upload-template', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // 匹配模板
  matchTemplate: (templateId, processedData, sessionId = null) => api.post('/data-processing/match-template', { 
    template_id: templateId, 
    processed_data: processedData,
    session_id: sessionId || undefined,
  }, { timeout: 60 * 60 * 1000 }),
  
  // 下载匹配的模板
  downloadMatchedTemplate: (resultId) => api.get(`/data-processing/download-matched-template/${resultId}`, { responseType: 'blob', timeout: 60 * 60 * 1000 }),
  
  // 删除当前模板
  deleteCurrentTemplate: () => api.delete('/data-processing/current-template'),
  
  // 获取处理提示信息
  getProcessingTips: () => api.get('/data-processing/processing-tips'),
  
  // 获取处理历史记录
  getHistory: () => api.get('/data-processing/history'),
  
  // 获取处理状态详情
  getProcessingStatus: (sessionId) => api.get(`/data-processing/processing-status/${sessionId}`),
  
  // 下载处理后的数据
  downloadProcessedData: (sessionId) => api.get(`/data-processing/download/${sessionId}`, { 
    responseType: 'blob', 
    timeout: 60 * 60 * 1000 
  }),
  
  // 下载模板匹配结果
  downloadTemplateResult: (resultId) => api.get(`/data-processing/download-template-result/${resultId}`, { 
    responseType: 'blob', 
    timeout: 60 * 60 * 1000 
  }),
};

// 认证相关API
export const authApi = {
  // 用户登录
  login: (data) => api.post('/auth/login/json', data),
  
  // 用户注册
  register: (data) => api.post('/auth/register', data),
  
  // 获取当前用户信息
  getCurrentUser: () => api.get('/auth/me'),
  
  // 刷新token
  refreshToken: () => api.post('/auth/refresh'),
  
  // 登出
  logout: () => api.post('/auth/logout'),
};

// 标题处理相关API
export const titleProcessingApi = {
  // 处理单个标题
  processSingleTitle: (title) => {
    console.log('调用单个标题处理API:', title);
    return api.post('/title-processing/analyze-single-title', { title });
  },
  
  // 批量处理标题
  processTitlesBatch: (titles) => {
    console.log('调用批量标题处理API:', titles.length);
    return api.post('/title-processing/process-titles-batch', { titles });
  },
  
  // 处理Excel文件
  processExcel: (file, titleColumn = null) => {
    console.log('调用Excel处理API:', file.name, titleColumn);
    const formData = new FormData();
    formData.append('file', file);
    if (titleColumn) {
      formData.append('title_column', titleColumn);
    }
    return api.post('/title-processing/process-excel', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // 预览处理结果
  previewProcessing: (file, titleColumn = null, sampleSize = 10) => {
    console.log('调用预览处理API:', file.name, titleColumn, sampleSize);
    const formData = new FormData();
    formData.append('file', file);
    if (titleColumn) {
      formData.append('title_column', titleColumn);
    }
    formData.append('sample_size', sampleSize.toString());
    return api.post('/title-processing/preview-processing', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  },
  
  // 导出处理结果
  exportResults: (results, filename) => {
    console.log('调用导出结果API:', results.length, filename);
    return api.post('/title-processing/export-results', {
      results,
      filename
    }, {
      responseType: 'blob'
    });
  },
  
  // 获取处理统计信息
  getStatistics: () => {
    console.log('调用统计信息API');
    return api.get('/title-processing/statistics');
  },
  
  // 获取处理历史
  getHistory: () => {
    console.log('调用处理历史API');
    return api.get('/title-processing/history');
  },
  
  // 恢复处理结果
  restoreResult: (sessionId) => {
    console.log('调用恢复结果API:', sessionId);
    return api.get(`/title-processing/restore/${sessionId}`);
  },
};

// 模板匹配相关API
export const templateMatchApi = {
  // 匹配数据到模板
  matchTemplate: (templateId, processedData, sessionId = null) => {
    return api.post('/data-processing/match-template', {
      template_id: templateId,
      processed_data: processedData,
      session_id: sessionId || undefined,
    });
  },

  // 获取模板匹配进度
  getProgress: (taskId) => {
    return api.get(`/data-processing/template-match-progress/${taskId}`);
  },

  // 获取用户的所有模板匹配任务
  getUserTasks: () => {
    return api.get('/data-processing/template-match-tasks');
  },

  // 取消模板匹配任务
  cancelTask: (taskId) => {
    return api.post(`/data-processing/cancel-template-match/${taskId}`);
  },

  // 下载模板匹配结果
  downloadResult: (resultId) => {
    return api.get(`/data-processing/download-template-result/${resultId}`, {
      responseType: 'blob'
    });
  }
};

// 管理员相关API
export const adminApi = {
  // 获取所有用户的处理历史
  getProcessingHistory: (params) => api.get('/admin/processing-history', { params }),
  
  // 下载用户处理数据Excel文件
  downloadUserData: (sessionId) => api.get(`/admin/processing-history/${sessionId}/download`, {
    responseType: 'blob',
    timeout: 60 * 60 * 1000
  }),
  
  // 获取用户最新的处理数据
  getUserLatestProcessing: (userId) => api.get(`/admin/users/${userId}/latest-processing`),
  
  // 获取用户列表
  getUsers: (params) => api.get('/admin/users', { params }),
  
  // 获取系统统计信息
  getStatistics: () => api.get('/admin/statistics'),
};


export default api;
